import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import {
  CheckIcon,
  XCircle,
  ChevronDown,
  XIcon,
  WandSparkles,
  X,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import AddTagToModifiableList from "../modal/AddTagToModifiableList";
import { ModifiableListContent } from "../form-items/ModifiableListContent";
import {
  dedupeModifiableTags,
  normalizeModifiableTagKey,
} from "@/lib/modifiable-multi-select";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { getMainLanguage } from "@/i18n";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    label: string;
    value: string;
    icon?: React.ReactNode;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Maximum number of selected badges to display. Extra items are summarized
   * as "+ N …". When set to 1, forces single selection mode.
   * Pass `null` to show every selected value (no "+ N more" truncation).
   * Optional, defaults to 3.
   */
  maxCount?: number | null;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * The key of the modifiable list to fetch options from.
   */
  listKey?: string;

  disableTagButton?: boolean;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = "Select options",
      animation = 0,
      maxCount = 3,
      modalPopover = false,
      className,
      listKey,
      disableTagButton = false,
      ...props
    },
    ref,
  ) => {
    const vocabulary = React.useMemo(
      () => options.map((option) => option.value),
      [options],
    );
    const [selectedValues, setSelectedValues] = React.useState<string[]>(() =>
      dedupeModifiableTags(defaultValue, vocabulary),
    );
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [isManageTagsOpen, setIsManageTagsOpen] = React.useState(false);

    const { t } = useTranslation();
    const vt = useVisibleT();
    const isUnlimited = maxCount == null;
    const visibleCount = isUnlimited
      ? selectedValues.length
      : Math.max(0, maxCount);
    const hiddenCount = isUnlimited
      ? 0
      : Math.max(0, selectedValues.length - visibleCount);

    const isValueSelected = React.useCallback(
      (value: string) => {
        const key = normalizeModifiableTagKey(value);
        return selectedValues.some(
          (selected) => normalizeModifiableTagKey(selected) === key,
        );
      },
      [selectedValues],
    );

    const resolveCanonicalValue = React.useCallback(
      (value: string) => {
        const key = normalizeModifiableTagKey(value);
        const fromOptions = options.find(
          (option) => normalizeModifiableTagKey(option.value) === key,
        );
        return fromOptions?.value.trim() ?? value.trim();
      },
      [options],
    );

    // Include selected values missing from options (legacy / free text) and
    // surface selected items first so "+ N more" values stay visible in the list.
    // Déduplique aussi les quasi-doublons (espaces / casse) — bug client listes.
    const displayOptions = React.useMemo(() => {
      const optionByNorm = new Map<
        string,
        { label: string; value: string; icon?: React.ReactNode }
      >();
      for (const option of options) {
        const trimmedValue = option.value.trim();
        if (!trimmedValue) continue;
        const key = normalizeModifiableTagKey(trimmedValue);
        if (optionByNorm.has(key)) continue;
        optionByNorm.set(key, {
          ...option,
          value: trimmedValue,
          label: option.label.trim() || trimmedValue,
        });
      }

      const selectedFirst: {
        label: string;
        value: string;
        icon?: React.ReactNode;
      }[] = [];
      const selectedNorms = new Set<string>();
      for (const value of selectedValues) {
        const trimmed = value.trim();
        if (!trimmed) continue;
        const key = normalizeModifiableTagKey(trimmed);
        if (selectedNorms.has(key)) continue;
        selectedNorms.add(key);
        selectedFirst.push(
          optionByNorm.get(key) ?? { label: trimmed, value: trimmed },
        );
      }

      const rest = [...optionByNorm.values()].filter(
        (option) =>
          !selectedNorms.has(normalizeModifiableTagKey(option.value)),
      );
      return [...selectedFirst, ...rest];
    }, [options, selectedValues]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const canonical = resolveCanonicalValue(option);
      const key = normalizeModifiableTagKey(canonical);

      if (maxCount === 1) {
        const newSelectedValues = isValueSelected(canonical) ? [] : [canonical];
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
        setIsPopoverOpen(false);
        return;
      }

      const newSelectedValues = isValueSelected(canonical)
        ? selectedValues.filter(
            (value) => normalizeModifiableTagKey(value) !== key,
          )
        : dedupeModifiableTags([...selectedValues, canonical], vocabulary);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen((prev) => !prev);
    };

    const clearExtraOptions = () => {
      if (isUnlimited) return;
      const newSelectedValues = selectedValues.slice(0, visibleCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const openManageTags = (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!listKey) return;
      setIsPopoverOpen(false);
      setIsManageTagsOpen(true);
    };

    return (
      <>
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild className="w-full">
          <Button
            ref={ref}
            {...props}
            size="multiselect"
            variant="multiselect"
            onClick={handleTogglePopover}
            className={cn(
              "flex min-h-9 h-fit w-full rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
              className,
            )}
          >
            {selectedValues.length > 0 ? (
              <div className="flex justify-between items-center w-full">
                <div
                  className={cn(
                    "flex flex-wrap items-center content-start",
                    // ~2 lignes de badges quand le champ est en pleine largeur
                    !isUnlimited && "max-h-[4.5rem] overflow-hidden",
                  )}
                >
                  {selectedValues.slice(0, visibleCount).map((value) => {
                    const selectedLabel =
                      options.find(
                        ({ value: v }) =>
                          normalizeModifiableTagKey(v) ===
                          normalizeModifiableTagKey(value),
                      )?.label ?? value;
                    return (
                      <Badge
                        key={normalizeModifiableTagKey(value)}
                        className={cn(
                          "shadow-none",
                          isAnimating ? "animate-bounce" : "",
                          multiSelectVariants({ variant }),
                        )}
                        style={{ animationDuration: `${animation}s` }}
                      >
                        {selectedLabel}
                        <X
                          className="ml-2 h-4 w-4 cursor-pointer text-gray-300"
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                        />
                      </Badge>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <Badge
                      className={cn(
                        "bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
                        isAnimating ? "animate-bounce" : "",
                        multiSelectVariants({ variant }),
                      )}
                      style={{ animationDuration: `${animation}s` }}
                    >
                      {t("bo.ui.multiSelect.more", {
                        count: hiddenCount,
                        defaultValue: "+ {{count}} de plus",
                      })}
                      <XCircle
                        className="ml-2 h-4 w-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation();
                          clearExtraOptions();
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        role="button"
                        tabIndex={-1}
                        aria-label={vt(
                          "bo.ui.multiSelect.clearAll",
                          "Supprimer tout",
                        )}
                        className="inline-flex"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          handleClear();
                        }}
                      >
                        <XIcon className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent variant="action" side="bottom" className="px-3">
                      <p>
                        {vt("bo.ui.multiSelect.clearAll", "Supprimer tout")}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  <Separator
                    orientation="vertical"
                    className="flex min-h-6 h-full"
                  />
                  <ChevronDown className="h-4 mx-2 cursor-pointer text-muted-foreground" />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full mx-auto">
                <span className="text-sm text-muted-foreground mx-3">
                  {placeholder}
                </span>
                <ChevronDown className="h-4 cursor-pointer text-muted-foreground mx-2" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command>
            <CommandInput
              placeholder="Search..."
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              <CommandEmpty>{vt("bo.ui.noResult", "Pas de résultat")}</CommandEmpty>
              {(() => {
                const selectedOptions = displayOptions.filter((o) =>
                  isValueSelected(o.value),
                );
                const otherOptions = displayOptions.filter(
                  (o) => !isValueSelected(o.value),
                );
                const renderOption = (
                  option: (typeof displayOptions)[number],
                ) => {
                  const isSelected = isValueSelected(option.value);
                  return (
                    <CommandItem
                      key={normalizeModifiableTagKey(option.value)}
                      value={option.label}
                      onSelect={() => toggleOption(option.value)}
                      className="cursor-pointer"
                    >
                      <div
                        className={cn(
                          "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible",
                        )}
                      >
                        <CheckIcon className="h-4 w-4 text-white" />
                      </div>
                      <span>{option.label}</span>
                    </CommandItem>
                  );
                };
                return (
                  <>
                    {selectedOptions.length > 0 && (
                      <CommandGroup
                        heading={vt("bo.ui.selected", "Sélectionnés")}
                      >
                        {selectedOptions.map(renderOption)}
                      </CommandGroup>
                    )}
                    {otherOptions.length > 0 && (
                      <CommandGroup>
                        {otherOptions.map(renderOption)}
                      </CommandGroup>
                    )}
                  </>
                );
              })()}
              <CommandSeparator />
              {!disableTagButton && (
                <div className="flex flex-col items-center justify-between border-t py-1">
                  {listKey && (
                    <AddTagToModifiableList
                      listKey={listKey}
                      lang={getMainLanguage()}
                      toggleOption={toggleOption}
                    />
                  )}
                  {listKey && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="flex-1 justify-center cursor-pointer max-w-full text-sm hover:bg-transparent hover:text-primary  text-[#005D8D] font-semibold"
                      onClick={openManageTags}
                    >
                      {t("multiSelectGererTags")}
                    </Button>
                  )}
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
        {animation > 0 && selectedValues.length > 0 && (
          <WandSparkles
            className={cn(
              "cursor-pointer my-2 text-foreground bg-background w-3 h-3",
              isAnimating ? "" : "text-muted-foreground",
            )}
            onClick={() => setIsAnimating(!isAnimating)}
          />
        )}
      </Popover>
      {listKey && (
        <Sheet open={isManageTagsOpen} onOpenChange={setIsManageTagsOpen}>
          <SheetContent className="w-[500px] px-0 max-h-lvh">
            <ModifiableListContent
              name={t("multiSelectGererTags")}
              listKey={listKey}
              selectedValues={selectedValues}
              onSelectValue={(value) => {
                if (!isValueSelected(value)) {
                  const next = dedupeModifiableTags(
                    [...selectedValues, resolveCanonicalValue(value)],
                    vocabulary,
                  );
                  setSelectedValues(next);
                  onValueChange(next);
                }
                setIsManageTagsOpen(false);
              }}
              lang={getMainLanguage()}
            />
          </SheetContent>
        </Sheet>
      )}
    </>
  );
  },
);

MultiSelect.displayName = "MultiSelect";
